import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function KinStarter() {
  const { user, persona } = useAuth();
  const navigate = useNavigate();
  const [hasKin, setHasKin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkKin = async () => {
      try {
        const res = await fetch('/api/groups/my', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.group) {
            setHasKin(true);
            // If they already have a Kin, redirect to dashboard
            navigate('/kin-dashboard');
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check Kin:', err);
      } finally {
        setLoading(false);
      }
    };
    checkKin();
  }, [navigate]);

  const isKinCoordinator = persona === 'buyer_kin_leader' || user?.role === 'buyer_kin_leader';

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Checking your Kin status...</div>
      </div>
    );
  }

  if (!isKinCoordinator) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <h1 className="text-3xl font-bold mb-4">🌟 Kin Coordinator Access Only</h1>
        <p className="text-gray-600 mb-6">
          You need to be registered as a Kin Coordinator to start a Kin group.
        </p>
        <Link to="/register" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">
          Register as Kin Coordinator
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold mb-4">🌟 Start Your Kin</h1>
        <p className="text-xl text-gray-600">
          Create a buying group with your community and save money on fresh food together
        </p>
      </div>

      {/* Step 1: What is a Kin? */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-green-100 text-green-700 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
            1
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">What is a Kin?</h2>
            <p className="text-gray-600">
              A Kin is a buying group of 5-20 people who live near each other and pool their orders
              to get better prices from local farms and hubs. By buying together, everyone saves money.
            </p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <span className="text-2xl block">💰</span>
                <span className="font-medium">Save 20-40%</span>
                <span className="text-xs text-gray-500 block">vs supermarket prices</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <span className="text-2xl block">🌱</span>
                <span className="font-medium">Fresh & Local</span>
                <span className="text-xs text-gray-500 block">Direct from farms</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <span className="text-2xl block">🤝</span>
                <span className="font-medium">Community</span>
                <span className="text-xs text-gray-500 block">Support local food</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <span className="text-2xl block">🚚</span>
                <span className="font-medium">Bulk Delivery</span>
                <span className="text-xs text-gray-500 block">One drop-off point</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: How it Works */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-green-100 text-green-700 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
            2
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">How a Kin Works</h2>
            <div className="space-y-3 text-gray-600">
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold">1.</span>
                <div>
                  <span className="font-medium">Create your Kin</span>
                  <p className="text-sm">Set up your group with a name, location, and invite your neighbours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold">2.</span>
                <div>
                  <span className="font-medium">Members join</span>
                  <p className="text-sm">Each member creates their own account and joins your Kin</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold">3.</span>
                <div>
                  <span className="font-medium">Everyone orders</span>
                  <p className="text-sm">Members browse products and add items to their individual draft</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold">4.</span>
                <div>
                  <span className="font-medium">Consolidate & order</span>
                  <p className="text-sm">You review all member drafts, consolidate by vendor, and place group orders</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold">5.</span>
                <div>
                  <span className="font-medium">Pay & receive</span>
                  <p className="text-sm">Members pay their share, goods are delivered to your drop-off point</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Your Role */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-green-100 text-green-700 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
            3
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">Your Role as Coordinator</h2>
            <div className="space-y-2 text-gray-600">
              <div className="flex items-center gap-2">
                <span className="text-green-600">🌟</span>
                <span>Manage members and invitations</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">📋</span>
                <span>Review and consolidate member orders</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">🔄</span>
                <span>Optimize orders for best discounts</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">📦</span>
                <span>Coordinate delivery and distribution</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600">💰</span>
                <span>Receive coordinator compensation (Volunteer, Food, or NFP)</span>
              </div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              💡 <span className="font-medium">Compensation:</span> Choose how you're rewarded - Volunteer (0%), Food (2% credit), or NFP (5% donation to your organisation)
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="text-center">
        <Link
          to="/kin-construction"
          className="inline-block bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition"
        >
          🚀 Start Your Kin Now
        </Link>
        <p className="text-sm text-gray-500 mt-2">
          Takes about 5 minutes to set up your Kin group
        </p>
      </div>

      {/* Already have a Kin? */}
      <div className="text-center mt-6">
        <Link to="/kin-dashboard" className="text-blue-600 hover:underline">
          Already have a Kin? Go to your dashboard →
        </Link>
      </div>
    </div>
  );
}
